/**
 * Sidebar variant of global search. Renders an input above the primary
 * nav and a popover dropdown directly underneath. The popover is
 * portaled to <body> with `position: fixed` so it overlays the sidebar
 * — escaping the ancestor `overflow-y: auto` on `.nav` that would
 * otherwise clip an absolutely-positioned popover and force the sidebar
 * to reflow. Cmd+K opens the centered palette instead (handled by
 * GlobalSearchProvider).
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { GlobalSearchResults } from './GlobalSearchResults';
import { useGlobalSearchController } from './useGlobalSearchController';
import styles from './GlobalSearch.module.css';

const POPOVER_WIDTH = 340;
const POPOVER_GAP = 6;

interface PopoverPos {
  top: number;
  left: number;
  width: number;
}

export function SidebarGlobalSearch() {
  const inputRowRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [focused, setFocused] = useState(false);
  const [pos, setPos] = useState<PopoverPos | null>(null);

  const controller = useGlobalSearchController({
    onClose: () => {
      inputRef.current?.blur();
      setFocused(false);
    },
  });

  // Track the input row's viewport rect so the portaled popover stays
  // anchored on scroll / resize. Uses capture-phase scroll so we catch
  // scrolls inside the nav as well as the document.
  useEffect(() => {
    if (!focused) return;
    const update = () => {
      const el = inputRowRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const width = Math.min(POPOVER_WIDTH, window.innerWidth - 16);
      setPos({
        top: Math.round(r.bottom + POPOVER_GAP),
        left: Math.round(r.left),
        width,
      });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [focused]);

  // Close on click outside BOTH the input row and the portaled popover.
  // Native useOnClickOutside only takes one ref — the portal lives
  // outside our root, so we need to OR the two containment checks.
  useEffect(() => {
    if (!focused) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (inputRowRef.current?.contains(t)) return;
      if (popoverRef.current?.contains(t)) return;
      setFocused(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [focused]);

  const showDropdown = focused;
  const listboxId = 'sidebar-gsearch-listbox';

  return (
    <div className={styles.sidebarRoot}>
      <div ref={inputRowRef} className={styles.sidebarInput}>
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={styles.searchIcon}
          aria-hidden
        >
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.35-4.35" />
        </svg>
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={showDropdown}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={
            showDropdown && controller.flatItems.length > 0
              ? `gsearch-item-${controller.activeIndex}`
              : undefined
          }
          placeholder="Search"
          value={controller.query}
          onChange={(e) => controller.setQuery(e.target.value)}
          onFocus={() => setFocused(true)}
          onKeyDown={controller.handleInputKeyDown}
          className={styles.input}
          autoComplete="off"
          spellCheck={false}
        />
        <span className={styles.kbdHint} aria-hidden>
          ⌘K
        </span>
      </div>

      {showDropdown &&
        pos &&
        createPortal(
          <div
            ref={popoverRef}
            className={styles.sidebarPopover}
            style={{ top: pos.top, left: pos.left, width: pos.width }}
          >
            <GlobalSearchResults
              search={controller.search}
              sections={controller.sections}
              activeIndex={controller.activeIndex}
              setActiveIndex={controller.setActiveIndex}
              onSelectItem={controller.selectItem}
              listboxId={listboxId}
            />
          </div>,
          document.body,
        )}
    </div>
  );
}
